import { Autocomplete, Box, CircularProgress, FormControl, FormHelperText, Grid, TextField } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { filter, find, first, groupBy, isEmpty, keyBy, map, orderBy, size } from 'lodash';
import React, { useEffect, useMemo, useState } from 'react';
import { Controller, Path, UseFormReturn } from 'react-hook-form';

import { getModel } from 'api/platform';
import { getStainTypeFilteredIds } from 'api/stainTypes';
import LabelledDropdown from 'components/atoms/Dropdown/LabelledDropdown';
import { ModelTableWithDefaultSelection } from 'components/ModelsTable/ModelTableWithDefaultSelection';
import {
  ModelType,
  modelTypeRunInferenceOptions,
  modelTypesByApiModelValue,
} from 'components/Pages/Jobs/inferenceFieldsOptions';
import { Model } from 'interfaces/model';
import { getStainingMethodByStainType, stainingMethodsList } from 'interfaces/stainType';
import { humanize } from 'utils/helpers';
import { getModelId } from 'utils/models';
import { useModelTypeOptions } from 'utils/queryHooks/useModelTypeOptions';
import { CasesParams } from 'utils/useCasesParams/types';
import { useStainTypeIdToDisplayName } from 'utils/useStainTypeIdToDisplayName';

export enum StainingMethodSelectionMode {
  inferred = 'inferred', // Don't allow direct selection, only infer based on chosen stain
  methodOnly = 'methodOnly', // Allow selection of staining method, and hide the stain type selection control
  filterStainsByMethod = 'filterStainsByMethod', // Show both controls and filter available stains by selected method
}

type DirectValueProp<Field extends string | undefined> = [Field] extends [undefined] ? string : undefined;

export interface SelectModelStepProps<
  FormFields extends Record<string, any>,
  ModelUrlField extends Path<FormFields>,
  ModelTypeField extends Path<FormFields> | undefined = undefined,
  SlideStainField extends Path<FormFields> | undefined = undefined,
  StainingMethodField extends Path<FormFields> | undefined = undefined
> {
  jobId?: string;
  casesParams?: CasesParams;
  debug?: boolean;
  onSelectModelUrl?: (modelUrl: string) => void;
  onDefaultModelUrlApplied?: (modelUrl: string) => void;
  onSelectStain?: (stainType: string) => void;
  onSelectModelType?: (modelType?: string) => void;
  stainingMethodSelectionMode?: StainingMethodSelectionMode;
  onSelectStainingMethod?: (stainingMethod: string) => void;
  formMethods: UseFormReturn<FormFields, any>;
  modelUrlField: ModelUrlField;
  modelTypeField?: ModelTypeField;
  slideStainField?: SlideStainField;
  stainingMethodField?: StainingMethodField;
  modelType?: DirectValueProp<ModelTypeField>;
  slideStain?: DirectValueProp<SlideStainField>;
  modelTypes?: ModelType[];
}

export const SelectModelStep = <
  FormFields extends Record<string, any>,
  ModelUrlField extends Path<FormFields>,
  ModelTypeField extends Path<FormFields> | undefined = undefined,
  SlideStainField extends Path<FormFields> | undefined = undefined,
  StainingMethodField extends Path<FormFields> | undefined = undefined
>({
  casesParams,
  debug = false,
  formMethods,
  modelType: modelTypeFromProps,
  slideStain: slideStainFromProps,
  modelUrlField,
  modelTypeField,
  slideStainField,
  stainingMethodField,
  onSelectModelUrl,
  onDefaultModelUrlApplied,
  onSelectStain,
  onSelectModelType,
  stainingMethodSelectionMode = StainingMethodSelectionMode.inferred,
  onSelectStainingMethod,
  modelTypes = modelTypeRunInferenceOptions,
}: React.PropsWithChildren<
  SelectModelStepProps<FormFields, ModelUrlField, ModelTypeField, SlideStainField, StainingMethodField>
>) => {
  const {
    control,
    reset,
    getValues,
    setValue,
    watch,
    formState: { errors },
  } = formMethods;
  const modelUrl = watch(modelUrlField);
  const modelType = modelTypeFromProps ?? (modelTypeField ? (watch(modelTypeField) as string | undefined) : undefined);
  const slideStain =
    slideStainFromProps ?? (slideStainField ? (watch(slideStainField) as string | undefined) : undefined);
  const [stainingMethodState, setStainingMethodState] = useState<string | undefined>(undefined);
  const stainingMethod = stainingMethodField
    ? (watch(stainingMethodField) as string | undefined) ?? stainingMethodState
    : stainingMethodState || getStainingMethodByStainType(slideStain);

  const { data: modelTypeOptions, isLoading: isLoadingModelTypeOptions } = useModelTypeOptions({ retry: false });
  const modelTypeOptionsByType = groupBy(modelTypeOptions, 'type');

  const { data: stains, isLoading: isLoadingStudyStains } = useQuery({
    queryKey: ['slidesStainsTypes', casesParams],
    queryFn: ({ signal }) => getStainTypeFilteredIds(casesParams as CasesParams, signal),
    enabled: Boolean(casesParams),
  });
  const { stainTypeIdToDisplayName, isLoadingStainTypeOptions, stainTypeOptions } = useStainTypeIdToDisplayName();

  const isLoadingStains = casesParams ? isLoadingStudyStains : isLoadingStainTypeOptions;

  // Updating staining method dropdown if a stain is selected and staining method field is not set
  useEffect(() => {
    if (stainingMethodField && !isLoadingStains && slideStain) {
      const newStainingMethod = getStainingMethodByStainType(slideStain);
      reset({ ...getValues(), [stainingMethodField]: newStainingMethod });
      onSelectStainingMethod?.(newStainingMethod);
    }
  }, [stainingMethodField, slideStain, isLoadingStains]);

  const onSelectModel = (model: Model) => {
    setValue(modelUrlField, model.url as any, { shouldValidate: true });
    onSelectModelUrl?.(model.url);
  };

  const modelTypesValues = keyBy(modelTypes, 'value');

  const modelId = getModelId(modelUrl);

  const { data: modelData } = useQuery({
    queryKey: ['model', modelId],
    queryFn: ({ signal }) => getModel(modelId as string, signal),
    retry: false,
    enabled: !isEmpty(modelUrl) && Boolean(modelId),
  });

  const [isSetModelType, setIsSetModelType] = useState(Boolean(modelTypeFromProps));

  useEffect(() => {
    if (modelTypeField && !isSetModelType && isEmpty(modelType) && !isEmpty(modelData?.meta?.modelType)) {
      const modelTypeFromModelData = modelData?.meta?.modelType
        ? modelTypesByApiModelValue[modelData.meta.modelType]?.value
        : undefined;
      reset({ ...getValues(), [modelTypeField]: modelTypeFromModelData });

      onSelectModelType?.(modelTypeFromModelData);
      setIsSetModelType(true);
    }
  }, [modelTypeField, modelData?.meta?.modelType, modelType, reset, getValues]);

  const [stainSearch, setStainSearch] = useState('');

  const handleStainingMethodChange = (method: string) => {
    if (!stainingMethodField) {
      setStainingMethodState(method);
    }
    onSelectStainingMethod?.(method);
  };

  const filteredStains = useMemo(() => {
    const stainsToShow = filter(
      casesParams ? stains : map(orderBy(stainTypeOptions, 'displayName'), 'id'),
      (stain) =>
        stainingMethodSelectionMode !== StainingMethodSelectionMode.filterStainsByMethod ||
        !stainingMethod ||
        getStainingMethodByStainType(stain) === stainingMethod
    );
    return map(stainsToShow, (currStain) => ({
      value: currStain,
      text: stainTypeIdToDisplayName(currStain),
    }));
  }, [stainingMethodSelectionMode, stainingMethod, stains, stainTypeOptions, stainTypeIdToDisplayName]);

  // Update stain type dropdown if there is only one stain type.
  // This is to avoid the case where the user has to select the only stain type.
  // UseEffect is used because defaultValues from useForm is not updating the dropdown.
  useEffect(() => {
    if (slideStainField && !isLoadingStains && size(filteredStains) === 1) {
      const newStain = first(filteredStains)?.value;
      const needsStainUpdate = slideStain !== newStain;
      const needsStainingMethodUpdate = Boolean(stainingMethodField && !stainingMethod && newStain);

      if (!newStain || (!needsStainUpdate && !needsStainingMethodUpdate)) {
        return;
      }

      const nextValues = {
        ...getValues(),
        [slideStainField]: newStain,
      } as Record<string, any>;

      if (needsStainingMethodUpdate && stainingMethodField) {
        nextValues[stainingMethodField] = getStainingMethodByStainType(newStain);
      }

      reset(nextValues as any);
      if (needsStainUpdate) {
        onSelectStain?.(newStain);
      }
    }
  }, [
    slideStainField,
    stainingMethodField,
    filteredStains,
    getValues,
    isLoadingStains,
    onSelectStain,
    reset,
    slideStain,
    stainingMethod,
  ]);

  return (
    <Grid container direction="column" spacing={2}>
      {Boolean((!modelTypeFromProps && modelTypeField) || (!slideStainFromProps && slideStainField)) && (
        <Grid item container spacing={2} direction="row">
          <Grid item container spacing={2} direction="row">
            {stainingMethodSelectionMode !== StainingMethodSelectionMode.inferred && (
              <Grid item xs={6}>
                {Boolean(stainingMethodField) && (
                  <Controller
                    control={control}
                    name={stainingMethodField!}
                    render={({ field: { onChange, value } }) => (
                      <LabelledDropdown
                        label="Staining Method"
                        options={map(stainingMethodsList, (method) => ({ value: method, text: humanize(method) }))}
                        value={value || ''}
                        disabled={Boolean(slideStainFromProps)} // If the slide stain is set from outside, don't allow changing it
                        onOptionSelected={(optionValue) => {
                          onChange(optionValue);

                          if (slideStain && getStainingMethodByStainType(slideStain) !== optionValue) {
                            if (slideStainField) {
                              setValue(slideStainField, '' as any);
                            }
                            onSelectStain?.('');
                          }
                          handleStainingMethodChange(optionValue);
                        }}
                        error={Boolean(errors[stainingMethodField!])}
                        helperText={
                          errors[stainingMethodField]?.message
                            ? humanize(errors[stainingMethodField]?.message as any)
                            : 'required before choosing model'
                        }
                        required
                      />
                    )}
                  />
                )}
              </Grid>
            )}

            {slideStainField && (isLoadingStains || size(filteredStains) > 1) && (
              <Grid item xs={6}>
                <Controller
                  control={control}
                  name={slideStainField}
                  render={({ field: { onChange, value } }) => (
                    <FormControl
                      fullWidth
                      error={Boolean(errors[slideStainField])}
                      required={stainingMethodSelectionMode === StainingMethodSelectionMode.inferred}
                    >
                      <Autocomplete<string, false, false, false>
                        loading={isLoadingStains}
                        options={map(filteredStains, 'value')}
                        getOptionLabel={(option) =>
                          find(stainTypeOptions, { id: option })?.displayName || stainTypeIdToDisplayName(option)
                        }
                        inputValue={stainSearch}
                        onInputChange={(_event, newMarkerTypeSearch) => setStainSearch(newMarkerTypeSearch)}
                        onChange={(_event, optionValue) => {
                          onChange(optionValue);
                          if (stainingMethodField) {
                            const newStainingMethod = getStainingMethodByStainType(optionValue || '');
                            setValue(stainingMethodField, newStainingMethod as any);
                            handleStainingMethodChange(newStainingMethod);
                          }
                          onSelectStain?.(optionValue || '');
                        }}
                        clearIcon={false}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            error={!isEmpty(errors[slideStainField]?.message)}
                            label={
                              isLoadingStains ? (
                                <Box sx={{ display: 'flex' }}>
                                  Stain <CircularProgress sx={{ marginLeft: 7 }} color="inherit" size={20} />
                                </Box>
                              ) : (
                                'Stain'
                              )
                            }
                          />
                        )}
                        disabled={size(filteredStains) <= 1}
                        value={value || null}
                      />
                      <FormHelperText>
                        {errors[slideStainField]?.message
                          ? humanize(errors[slideStainField]?.message as any)
                          : stainingMethodSelectionMode === StainingMethodSelectionMode.inferred
                          ? size(filteredStains) > 1
                            ? 'There is more than one stain type, please select one'
                            : 'required before choosing model'
                          : false}
                      </FormHelperText>
                    </FormControl>
                  )}
                />
              </Grid>
            )}
          </Grid>
          {!modelTypeFromProps && modelTypeField && (
            <Grid item xs={6}>
              <Controller
                control={control}
                name={modelTypeField}
                render={({ field: { onChange, value } }) => (
                  <LabelledDropdown
                    label="Model Type"
                    options={modelTypes}
                    value={value || ''}
                    disabled={!stainingMethod}
                    onOptionSelected={(optionValue) => {
                      const matchingModelTypeRunInferenceOption = find(modelTypeRunInferenceOptions, {
                        value: optionValue,
                      });
                      const modelTypeOptionsWithUrls = filter(
                        modelTypeOptionsByType[matchingModelTypeRunInferenceOption?.apiModelValue || optionValue],
                        ({ defaultModelUrl }) => Boolean(defaultModelUrl)
                      );

                      const matchingModelType =
                        find(
                          modelTypeOptionsWithUrls,
                          (modelTypeOption) => modelTypeOption.stainingMethod === stainingMethod
                        ) || find(modelTypeOptionsWithUrls, (modelTypeOption) => !modelTypeOption.stainingMethod);

                      if (debug) {
                        console.debug(
                          'Updating default model url to',
                          matchingModelType?.defaultModelUrl || '',
                          'for model type',
                          matchingModelTypeRunInferenceOption?.apiModelValue || optionValue,
                          'with staining method',
                          stainingMethod,
                          {
                            modelTypeOptionsByType,
                            matchingModelType,
                            optionValue,
                            matchingModelTypeRunInferenceOption,
                          }
                        );
                      }
                      setValue(modelUrlField, (matchingModelType?.defaultModelUrl || '') as any);
                      onChange(optionValue);

                      onSelectModelType?.(optionValue);
                    }}
                    error={Boolean(errors[modelTypeField])}
                    helperText={
                      errors[modelTypeField]?.message
                        ? humanize(errors[modelTypeField]?.message as any)
                        : 'required before choosing model'
                    }
                    loading={isLoadingModelTypeOptions}
                    required
                  />
                )}
              />
            </Grid>
          )}
        </Grid>
      )}
      <ModelTableWithDefaultSelection
        modelType={modelType ? modelTypesValues[modelType]?.apiModelValue || modelType : modelType}
        stainType={slideStain}
        stainingMethod={getStainingMethodByStainType(slideStain)}
        onDefaultModelUrlChange={(defaultModelUrl) => {
          setValue(modelUrlField, defaultModelUrl as any, { shouldValidate: true });
          onSelectModelUrl?.(defaultModelUrl);
          onDefaultModelUrlApplied?.(defaultModelUrl);
        }}
        onSelect={onSelectModel}
        onSelectedModelUrlChange={(newModelUrl) => {
          setValue(modelUrlField, newModelUrl as any, { shouldValidate: true });
          onSelectModelUrl?.(newModelUrl);
        }}
        selectedModelUrl={modelUrl}
        enabled={!isEmpty(slideStain) && !isEmpty(modelType)}
        hasModelUrlError={Boolean(errors[modelUrlField])}
        modelUrlErrorMessage={humanize(errors[modelUrlField]?.message as any)}
      />
    </Grid>
  );
};
